import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clamp, colorWithAlpha, mixColors, parseColorToRgb } from './liquidShared';
import './WaterPipe.css';

const VIEWBOX_WIDTH = 320;
const VIEWBOX_HEIGHT = 120;

const LEGACY_WATER_COLORS = ['#00BFFF', '#00bfff'];
const LEGACY_PIPE_COLORS = ['rgba(255, 255, 255, 0.08)', 'rgba(255,255,255,0.08)'];
const LEGACY_FRAME_BG_COLORS = [
  'rgba(18, 28, 40, 0.55)',
  'rgba(18,28,40,0.55)',
  'rgba(23, 37, 64, 0.96)',
  'rgba(23,37,64,0.96)',
];
const LEGACY_FRAME_BORDER_COLORS = [
  'rgba(0, 202, 214, 0.35)',
  'rgba(0,202,214,0.35)',
  'rgba(24, 99, 129, 0.76)',
  'rgba(24,99,129,0.76)',
];
const LEGACY_PIPE_BORDER_COLORS = ['rgba(255, 255, 255, 0.16)', 'rgba(255,255,255,0.16)'];
const LEGACY_CLAMP_COLORS = ['rgba(125, 170, 170, 0.65)', 'rgba(125,170,170,0.65)'];

const colorsEqual = (left, right) => {
  const rgbLeft = parseColorToRgb(left);
  const rgbRight = parseColorToRgb(right);
  if (!rgbLeft || !rgbRight) {
    return String(left || '').trim() === String(right || '').trim();
  }

  return rgbLeft.every((channel, index) => channel === rgbRight[index]);
};

const matchesAnyColor = (value, candidates) =>
  candidates.some((candidate) => colorsEqual(value, candidate));

const normalizeColor = (value, fallback, legacyValues = []) => {
  const normalized = String(value || '').trim();
  if (!normalized || matchesAnyColor(normalized, legacyValues)) {
    return fallback;
  }
  return normalized;
};

const createWaveFillPath = ({
  xStart,
  xEnd,
  baseY,
  amplitude,
  wavelength,
  floorY,
}) => {
  let x = xStart;
  let d = `M${xStart} ${baseY}`;

  while (x < xEnd) {
    const nextX = Math.min(x + wavelength, xEnd);
    const span = nextX - x;
    const quarter = span * 0.25;
    const half = span * 0.5;

    d += ` C ${x + quarter} ${baseY - amplitude}, ${x + half - quarter} ${baseY - amplitude}, ${x + half} ${baseY}`;
    d += ` C ${x + half + quarter} ${baseY + amplitude}, ${nextX - quarter} ${baseY + amplitude}, ${nextX} ${baseY}`;
    x = nextX;
  }

  d += ` L ${xEnd} ${floorY} L ${xStart} ${floorY} Z`;
  return d;
};

const createWaveBandPath = ({
  xStart,
  xEnd,
  topY,
  baseY,
  amplitude,
  wavelength,
}) => {
  let x = xEnd;
  let d = `M${xStart} ${topY} L ${xEnd} ${topY} L ${xEnd} ${baseY}`;

  while (x > xStart) {
    const nextX = Math.max(x - wavelength, xStart);
    const span = x - nextX;
    const quarter = span * 0.25;
    const half = span * 0.5;

    d += ` C ${x - quarter} ${baseY + amplitude}, ${x - half + quarter} ${baseY + amplitude}, ${x - half} ${baseY}`;
    d += ` C ${x - half - quarter} ${baseY - amplitude}, ${nextX + quarter} ${baseY - amplitude}, ${nextX} ${baseY}`;
    x = nextX;
  }

  d += ' Z';
  return d;
};

const hasStreamValue = (value, threshold = 0) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const numericValue = Number(trimmed);
    if (Number.isFinite(numericValue)) {
      return Math.abs(numericValue) > Math.abs(Number(threshold) || 0);
    }
    return !['false', 'off', 'no', 'none', 'null', 'undefined'].includes(trimmed.toLowerCase());
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && Math.abs(value) > Math.abs(Number(threshold) || 0);
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const resolveFlowDirection = (direction, flowDirection) => {
  const normalizedDirection = String(direction || 'horizontal').trim().toLowerCase();
  const normalizedFlowDirection = String(flowDirection || '').trim().toLowerCase();

  if (normalizedDirection === 'vertical') {
    return normalizedFlowDirection === 'bottom-to-top' ? 'bottom-to-top' : 'top-to-bottom';
  }

  return normalizedFlowDirection === 'right-to-left' ? 'right-to-left' : 'left-to-right';
};

const WaterPipe = ({
  title,
  transparentBackground,
  value,
  color,
  pipeColor,
  pipeBackgroundColor,
  pipeGlassColor,
  showLabel,
  onLabel,
  offLabel,
  direction = 'horizontal',
  flowDirection = 'left-to-right',
  draggable,
  dims,
  frameBg,
  frameBorderColor,
  frameBorderWidth = 2,
  frameRadius = 18,
  framePadding = 0,
  pipeHeight = 70,
  pipeWidthPercent = 100,
  pipeBorderColor,
  pipeBorderWidth = 10,
  pipeRadius = 18,
  clampCount = 4,
  clampColor,
  clampWidth = 24,
  clampRadius = 16,
  waterOpacity = 0.85,
  waveSpeed = 2.6,
  flowThreshold = 0,
  titleFontSize = 34,
  statusFontSize = 30,
  embedded = false,
  className = '',
  style,
}) => {
  const [isFlowing, setIsFlowing] = useState(() => hasStreamValue(value, flowThreshold));
  const graphicRef = useRef(null);
  const [graphicBounds, setGraphicBounds] = useState(() => ({
    width: Number.isFinite(Number(dims?.width)) ? Number(dims.width) : 340,
    height: Number.isFinite(Number(dims?.height)) ? Number(dims.height) : 260,
  }));

  useEffect(() => {
    setIsFlowing(hasStreamValue(value, flowThreshold));
  }, [flowThreshold, value]);

  const isVertical = direction === 'vertical';
  const resolvedFlowDirection = useMemo(
    () => resolveFlowDirection(direction, flowDirection),
    [direction, flowDirection]
  );
  const usesReverseAnimationDirection = useMemo(() => {
    if (isVertical) {
      return resolvedFlowDirection === 'top-to-bottom';
    }

    return resolvedFlowDirection === 'left-to-right';
  }, [isVertical, resolvedFlowDirection]);
  const instanceIdRef = useRef(`water-pipe-${Math.random().toString(36).slice(2, 10)}`);

  const widgetWidth = Number.isFinite(Number(dims?.width)) ? Number(dims.width) : 340;
  const widgetHeight = Number.isFinite(Number(dims?.height)) ? Number(dims.height) : 260;

  useEffect(() => {
    const element = graphicRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateBounds = (entry) => {
      const nextWidth = Math.max(1, Math.round(entry?.contentRect?.width || element.clientWidth || widgetWidth));
      const nextHeight = Math.max(1, Math.round(entry?.contentRect?.height || element.clientHeight || widgetHeight));

      setGraphicBounds((previous) => {
        if (previous.width === nextWidth && previous.height === nextHeight) {
          return previous;
        }

        return {
          width: nextWidth,
          height: nextHeight,
        };
      });
    };

    updateBounds();

    const observer = new ResizeObserver((entries) => {
      const nextEntry = entries[0];
      if (!nextEntry) return;
      updateBounds(nextEntry);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [widgetHeight, widgetWidth]);
  const defaultWaterColor = useMemo(
    () => mixColors('var(--icon-reset-password)', 'var(--part-color)', 0.28),
    []
  );
  const defaultPipeColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.24),
    []
  );
  const defaultPipeGlassColor = useMemo(
    () => mixColors('var(--part-color)', 'var(--cell-background)', 0.24),
    []
  );
  const defaultFrameBg = useMemo(
    () => 'var(--header-background)',
    []
  );
  const defaultFrameBorderColor = useMemo(
    () => 'var(--widget-container-border)',
    []
  );
  const defaultPipeBorderColor = useMemo(
    () => mixColors(defaultPipeColor, 'var(--part-color)', 0.28),
    [defaultPipeColor]
  );
  const defaultClampColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.18),
    []
  );

  const resolvedWaterColor = useMemo(
    () => normalizeColor(color, defaultWaterColor, LEGACY_WATER_COLORS),
    [color, defaultWaterColor]
  );

  const resolvedPipeColor = useMemo(
    () => normalizeColor(pipeColor, defaultPipeColor, LEGACY_PIPE_COLORS),
    [defaultPipeColor, pipeColor]
  );

  const defaultPipeBackgroundColor = useMemo(
    () => mixColors('var(--header-background)', resolvedPipeColor, 0.14),
    [resolvedPipeColor]
  );

  const resolvedPipeBackgroundColor = useMemo(
    () => normalizeColor(pipeBackgroundColor, defaultPipeBackgroundColor),
    [defaultPipeBackgroundColor, pipeBackgroundColor]
  );

  const resolvedPipeGlassColor = useMemo(
    () =>
      normalizeColor(
        pipeGlassColor,
        defaultPipeGlassColor,
        [...LEGACY_PIPE_COLORS, defaultPipeColor]
      ),
    [defaultPipeColor, defaultPipeGlassColor, pipeGlassColor]
  );

  const resolvedFrameBg = useMemo(
    () => normalizeColor(frameBg, defaultFrameBg, LEGACY_FRAME_BG_COLORS),
    [defaultFrameBg, frameBg]
  );

  const resolvedFrameBorderColor = useMemo(
    () =>
      normalizeColor(
        frameBorderColor,
        defaultFrameBorderColor,
        LEGACY_FRAME_BORDER_COLORS
      ),
    [defaultFrameBorderColor, frameBorderColor]
  );

  const resolvedPipeBorderColor = useMemo(
    () =>
      normalizeColor(
        pipeBorderColor,
        defaultPipeBorderColor,
        LEGACY_PIPE_BORDER_COLORS
      ),
    [defaultPipeBorderColor, pipeBorderColor]
  );

  const resolvedClampColor = useMemo(
    () => normalizeColor(clampColor, defaultClampColor, LEGACY_CLAMP_COLORS),
    [clampColor, defaultClampColor]
  );
  const interiorBaseColor = resolvedPipeBackgroundColor;

  const shellLight = useMemo(
    () => mixColors(resolvedPipeColor, 'var(--part-color)', 0.34),
    [resolvedPipeColor]
  );
  const shellDark = useMemo(
    () => mixColors(resolvedPipeColor, 'var(--shadow-black-soft)', 0.28),
    [resolvedPipeColor]
  );
  const glassGlow = useMemo(
    () => mixColors(resolvedPipeGlassColor, 'var(--part-color)', 0.42),
    [resolvedPipeGlassColor]
  );
  const cavityLight = useMemo(
    () => mixColors('var(--header-background)', interiorBaseColor, isFlowing ? 0.22 : 0.08),
    [interiorBaseColor, isFlowing]
  );
  const cavityDark = useMemo(
    () => mixColors('var(--shadow-black-soft)', interiorBaseColor, isFlowing ? 0.08 : 0.18),
    [interiorBaseColor, isFlowing]
  );
  const waterLight = useMemo(
    () => mixColors(resolvedWaterColor, 'var(--part-color)', 0.54),
    [resolvedWaterColor]
  );
  const waterMid = useMemo(
    () => mixColors(resolvedWaterColor, 'var(--header-background)', 0.18),
    [resolvedWaterColor]
  );
  const waterDark = useMemo(
    () => mixColors(resolvedWaterColor, 'var(--shadow-black-soft)', 0.34),
    [resolvedWaterColor]
  );
  const clampLight = useMemo(
    () => mixColors(resolvedClampColor, 'var(--part-color)', 0.18),
    [resolvedClampColor]
  );
  const clampDark = useMemo(
    () => mixColors(resolvedClampColor, 'var(--shadow-black-soft)', 0.24),
    [resolvedClampColor]
  );
  const pipeSurfaceHighlight = useMemo(
    () => colorWithAlpha(mixColors(resolvedPipeBorderColor, 'var(--part-color)', 0.48), 0.46),
    [resolvedPipeBorderColor]
  );
  const clampInnerHighlight = useMemo(
    () => colorWithAlpha(mixColors(resolvedClampColor, 'var(--part-color)', 0.42), 0.42),
    [resolvedClampColor]
  );

  const rawFramePadding = Number(framePadding);
  const safeFramePadding = (Number.isFinite(rawFramePadding) && rawFramePadding !== 16)
    ? clamp(rawFramePadding, 0, 34)
    : 0;
  const safeFrameRadius = clamp(Number(frameRadius) || 18, 12, 34);
  const safeFrameBorderWidth = clamp(Number(frameBorderWidth) || 2, 1, 4);
  const safeWaterOpacity = clamp(Number(waterOpacity) || 0.85, 0.28, 1);
  const safeWaveSpeed = clamp(Number(waveSpeed) || 2.6, 0.8, 12);
  const rawPipeWidthPercent = Number(pipeWidthPercent);
  const normalizedPipeWidthPercent = (!Number.isFinite(rawPipeWidthPercent) || rawPipeWidthPercent === 62)
    ? 100
    : rawPipeWidthPercent;
  const safePipeWidthPercent = clamp(normalizedPipeWidthPercent, 20, 100);
  const safeClampCount = clamp(Math.round(Number(clampCount) || 4), 2, 8);

  const graphicAreaWidth = Math.max(1, Math.round(graphicBounds.width || widgetWidth));
  const graphicAreaHeight = Math.max(1, Math.round(graphicBounds.height || widgetHeight));
  const svgBoxWidth = useMemo(() => {
    const primaryAxis = isVertical ? graphicAreaHeight : graphicAreaWidth;
    return Math.round(Math.max(1, primaryAxis * (safePipeWidthPercent / 100)));
  }, [graphicAreaHeight, graphicAreaWidth, isVertical, safePipeWidthPercent]);
  const svgBoxHeight = useMemo(
    () => Math.round(Math.max(1, isVertical ? graphicAreaWidth : graphicAreaHeight)),
    [graphicAreaHeight, graphicAreaWidth, isVertical]
  );

  const titleSizePx = useMemo(
    () => Math.round(clamp(Number(titleFontSize) || 34, 8, 140)),
    [titleFontSize]
  );

  const labelSizePx = useMemo(
    () => Math.round(clamp(Number(statusFontSize) || 30, 8, 140)),
    [statusFontSize]
  );

  const waveAmplitude = isFlowing ? 4.4 : 3.2;
  const waveDuration = isFlowing ? safeWaveSpeed : safeWaveSpeed * 1.55;
  const secondaryWaveDuration = waveDuration * 1.32;
  const sheenDuration = waveDuration * 1.18;
  const glassDuration = waveDuration * 1.9;

  const safePipeHeight = clamp(Number(pipeHeight) || 70, 20, 108);
  const outerHeight = clamp(safePipeHeight * 0.66, 16, 62);
  const outerY = (VIEWBOX_HEIGHT - outerHeight) / 2;
  const outerX = 0;
  const outerWidth = VIEWBOX_WIDTH;
  const outerRadius = clamp((Number(pipeRadius) || 18) * 1.05, outerHeight / 3, outerHeight / 2);
  const pipeThicknessScale = outerHeight / 46.2;

  const effectivePipeBorderWidth = (Number(pipeBorderWidth) || 10) * pipeThicknessScale;
  const innerInset = clamp(effectivePipeBorderWidth * 0.5, 3, 11);
  const innerX = outerX + innerInset;
  const innerY = outerY + innerInset + 0.5;
  const innerWidth = outerWidth - innerInset * 2;
  const innerHeight = outerHeight - innerInset * 2 - 1;
  const innerRadius = clamp(outerRadius - innerInset * 0.72, innerHeight / 3, innerHeight / 2);

  const clampBodyWidth = clamp((Number(clampWidth) || 24) * 0.56 * pipeThicknessScale, 5, 26);
  const clampBodyHeight = outerHeight + (18 * Math.max(0.58, pipeThicknessScale));
  const clampBodyY = (VIEWBOX_HEIGHT - clampBodyHeight) / 2;
  const clampBodyRadius = clamp((Number(clampRadius) || 16) * 0.48 * pipeThicknessScale, clampBodyWidth / 2, 14);
  const clampInnerWidth = clampBodyWidth * 0.52;
  const clampInnerHeight = clampBodyHeight * 0.72;

  const clampCenters = useMemo(() => {
    if (safeClampCount <= 1) {
      return [outerX + outerWidth / 2];
    }

    const edgeInset = Math.max(clampBodyWidth / 2 + 4, 14);
    return Array.from({ length: safeClampCount }, (_, index) => {
      const ratio = index / (safeClampCount - 1);
      return outerX + edgeInset + ratio * (outerWidth - edgeInset * 2);
    });
  }, [clampBodyWidth, outerWidth, outerX, safeClampCount]);

  const waveXStart = innerX - 54;
  const waveXEnd = innerX + innerWidth + 72;
  const waterBaseY = innerY + innerHeight * (isFlowing ? 0.5 : 0.52);
  const waterFloorY = innerY + innerHeight + 10;

  const primaryWavePath = useMemo(
    () =>
      createWaveFillPath({
        xStart: waveXStart,
        xEnd: waveXEnd,
        baseY: waterBaseY,
        amplitude: waveAmplitude,
        wavelength: 44,
        floorY: waterFloorY,
      }),
    [waterBaseY, waterFloorY, waveAmplitude, waveXEnd, waveXStart]
  );

  const secondaryWavePath = useMemo(
    () =>
      createWaveFillPath({
        xStart: waveXStart - 10,
        xEnd: waveXEnd + 16,
        baseY: waterBaseY - 1.2,
        amplitude: waveAmplitude * 0.72,
        wavelength: 36,
        floorY: waterFloorY,
      }),
    [waterBaseY, waterFloorY, waveAmplitude, waveXEnd, waveXStart]
  );

  const highlightBandPath = useMemo(
    () =>
      createWaveBandPath({
        xStart: innerX - 18,
        xEnd: innerX + innerWidth + 26,
        topY: innerY + 2,
        baseY: innerY + innerHeight * 0.34,
        amplitude: 2.4,
        wavelength: 42,
      }),
    [innerHeight, innerWidth, innerX, innerY]
  );

  const highlightShadowPath = useMemo(
    () =>
      createWaveBandPath({
        xStart: innerX - 18,
        xEnd: innerX + innerWidth + 30,
        topY: innerY + innerHeight * 0.3,
        baseY: innerY + innerHeight * 0.43,
        amplitude: 1.8,
        wavelength: 44,
      }),
    [innerHeight, innerWidth, innerX, innerY]
  );

  const svgIdBase = useMemo(
    () =>
      `${String(title || 'water-pipe').replace(/[^A-Za-z0-9_-]/g, '_')}-${instanceIdRef.current}-${isVertical ? 'vertical' : 'horizontal'}`,
    [isVertical, title]
  );

  const shellGradientId = `${svgIdBase}-shell`;
  const cavityGradientId = `${svgIdBase}-cavity`;
  const waterGradientId = `${svgIdBase}-water`;
  const clampGradientId = `${svgIdBase}-clamp`;
  const glossGradientId = `${svgIdBase}-gloss`;
  const clipPathId = `${svgIdBase}-clip`;

  const containerClassName = [
    'widget-container',
    'water-pipe-widget',
    draggable ? 'is-draggable' : 'not-draggable',
    transparentBackground ? 'is-transparent' : '',
    isVertical ? 'is-vertical' : 'is-horizontal',
    isFlowing ? 'is-flowing' : 'is-idle',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const containerStyle = useMemo(
    () => ({
      '--water-pipe-frame-bg': resolvedFrameBg,
      '--water-pipe-frame-border-color': resolvedFrameBorderColor,
      '--water-pipe-frame-border-width': `${safeFrameBorderWidth}px`,
      '--water-pipe-frame-radius': `${safeFrameRadius}px`,
      '--water-pipe-frame-padding': `${safeFramePadding}px`,
      '--water-pipe-wave-duration': `${waveDuration}s`,
      '--water-pipe-wave-secondary-duration': `${secondaryWaveDuration}s`,
      '--water-pipe-sheen-duration': `${sheenDuration}s`,
      '--water-pipe-glass-duration': `${glassDuration}s`,
      '--water-pipe-flow-animation-direction': usesReverseAnimationDirection ? 'reverse' : 'normal',
      '--water-pipe-title-size': `${titleSizePx}px`,
      '--water-pipe-label-size': `${labelSizePx}px`,
      '--water-pipe-glow-color': colorWithAlpha(resolvedFrameBorderColor, 0.2),
    }),
    [
      glassDuration,
      labelSizePx,
      resolvedFrameBg,
      resolvedFrameBorderColor,
      safeFrameBorderWidth,
      safeFramePadding,
      safeFrameRadius,
      sheenDuration,
      secondaryWaveDuration,
      titleSizePx,
      usesReverseAnimationDirection,
      waveDuration,
    ]
  );

  const setGrabbing = (event, grabbing) => {
    if (event.target.closest('.widget-buttons-container')) return;
    if (embedded || !draggable) return;
    const element = event.target.closest('.water-pipe-widget');
    if (!element) return;
    element.classList.toggle('is-grabbing', Boolean(grabbing));
  };

  const statusLabel = isFlowing ? (onLabel || 'Water Flowing') : (offLabel || 'No Flow');

  return (
    <div
      className={containerClassName}
      style={{ ...containerStyle, ...style }}
      onMouseDown={(event) => setGrabbing(event, true)}
      onMouseUp={(event) => setGrabbing(event, false)}
      onMouseLeave={(event) => setGrabbing(event, false)}
      draggable={embedded ? false : draggable}
      onDragStart={(event) => {
        if (embedded) return;
        const widget = event.target.closest('.widget');
        const id = widget?.id;
        if (!id) return;

        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.x;
        const offsetY = event.clientY - rect.y;

        let dragData = `pipe;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        event.dataTransfer.setData('text/plain', dragData);
      }}
    >
      {!embedded ? <h3 className="water-pipe-widget__title">{title}</h3> : null}

      <div ref={graphicRef} className="water-pipe-widget__graphic">
        <div className="water-pipe-stage">
          <svg
            className={`water-pipe-stage__svg${isVertical ? ' is-vertical' : ''}`}
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            style={{
              width: `${svgBoxWidth}px`,
              height: `${svgBoxHeight}px`,
            }}
          >
            <defs>
              <linearGradient
                id={shellGradientId}
                x1="34"
                y1="32"
                x2="286"
                y2="88"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={colorWithAlpha(shellLight, 0.96)} />
                <stop offset="50%" stopColor={colorWithAlpha(resolvedPipeColor, 0.94)} />
                <stop offset="100%" stopColor={colorWithAlpha(shellDark, 0.98)} />
              </linearGradient>

              <linearGradient
                id={cavityGradientId}
                x1="44"
                y1="42"
                x2="276"
                y2="76"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={colorWithAlpha(cavityLight, 0.9)} />
                <stop offset="100%" stopColor={colorWithAlpha(cavityDark, 0.96)} />
              </linearGradient>

              <linearGradient
                id={waterGradientId}
                x1="52"
                y1="44"
                x2="52"
                y2="84"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={colorWithAlpha(waterLight, safeWaterOpacity)} />
                <stop offset="56%" stopColor={colorWithAlpha(waterMid, safeWaterOpacity)} />
                <stop offset="100%" stopColor={colorWithAlpha(waterDark, safeWaterOpacity)} />
              </linearGradient>

              <linearGradient
                id={clampGradientId}
                x1="24"
                y1="28"
                x2="24"
                y2="92"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={clampLight} />
                <stop offset="100%" stopColor={clampDark} />
              </linearGradient>

              <linearGradient
                id={glossGradientId}
                x1="42"
                y1="36"
                x2="278"
                y2="60"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={colorWithAlpha(glassGlow, 0.46)} />
                <stop offset="100%" stopColor={colorWithAlpha(glassGlow, 0.08)} />
              </linearGradient>

              <clipPath id={clipPathId}>
                <rect
                  x={innerX}
                  y={innerY}
                  width={innerWidth}
                  height={innerHeight}
                  rx={innerRadius}
                />
              </clipPath>
            </defs>

            <g>
              <rect
                x={outerX}
                y={outerY}
                width={outerWidth}
                height={outerHeight}
                rx={outerRadius}
                fill={`url(#${shellGradientId})`}
              />

              <rect
                className="water-pipe-stage__body-sheen"
                x={outerX + 10}
                y={outerY + 4}
                width={outerWidth * 0.72}
                height={Math.max(10, outerHeight * 0.24)}
                rx={Math.max(5, outerHeight * 0.12)}
                fill={`url(#${glossGradientId})`}
                opacity="0.62"
              />

              <rect
                x={innerX}
                y={innerY}
                width={innerWidth}
                height={innerHeight}
                rx={innerRadius}
                fill={`url(#${cavityGradientId})`}
              />

              <g clipPath={`url(#${clipPathId})`}>
                <rect
                  x={innerX}
                  y={innerY}
                  width={innerWidth}
                  height={innerHeight * 0.42}
                  rx={innerRadius}
                  fill={colorWithAlpha(resolvedPipeGlassColor, 0.2)}
                />

                {isFlowing ? (
                  <React.Fragment>
                    <path
                      className="water-pipe-stage__wave water-pipe-stage__wave--primary"
                      d={primaryWavePath}
                      fill={`url(#${waterGradientId})`}
                    />

                    <path
                      className="water-pipe-stage__wave water-pipe-stage__wave--secondary"
                      d={secondaryWavePath}
                      fill={colorWithAlpha(waterLight, safeWaterOpacity * 0.32)}
                    />

                    <path
                      className="water-pipe-stage__surface water-pipe-stage__surface--shadow"
                      d={highlightShadowPath}
                      fill={colorWithAlpha(waterDark, 0.18)}
                    />

                    <path
                      className="water-pipe-stage__surface water-pipe-stage__surface--highlight"
                      d={highlightBandPath}
                      fill={pipeSurfaceHighlight}
                    />
                  </React.Fragment>
                ) : null}
              </g>

              <rect
                x={innerX}
                y={innerY}
                width={innerWidth}
                height={innerHeight}
                rx={innerRadius}
                fill="none"
                stroke={colorWithAlpha(resolvedPipeBorderColor, 0.56)}
                strokeWidth="1.8"
              />

              {clampCenters.map((centerX) => {
                const innerHighlightX = centerX - clampInnerWidth / 2;
                const innerHighlightY = clampBodyY + (clampBodyHeight - clampInnerHeight) / 2;

                return (
                  <g key={`clamp-${centerX}`}>
                    <rect
                      x={centerX - clampBodyWidth / 2}
                      y={clampBodyY}
                      width={clampBodyWidth}
                      height={clampBodyHeight}
                      rx={clampBodyRadius}
                      fill={`url(#${clampGradientId})`}
                    />
                    <rect
                      x={innerHighlightX}
                      y={innerHighlightY}
                      width={clampInnerWidth}
                      height={clampInnerHeight}
                      rx={clampBodyRadius * 0.7}
                      fill={clampInnerHighlight}
                    />
                  </g>
                );
              })}

              <rect
                x={outerX}
                y={outerY}
                width={outerWidth}
                height={outerHeight}
                rx={outerRadius}
                fill="none"
                stroke={colorWithAlpha(resolvedPipeBorderColor, 0.34)}
                strokeWidth="1.5"
              />
            </g>
          </svg>
        </div>
      </div>

      {showLabel ? <span className="water-pipe-widget__label">{statusLabel}</span> : null}
    </div>
  );
};

export default WaterPipe;
